import { NATIONALITIES } from '@multirx/shared/types/nationalities.js';
import { createDriver } from './api.js';
import { createPillButton } from '../ui/pillButton.js';
import type { Driver } from '@multirx/shared';

export type CreateDriverModal = {
  open: () => void;
  close: () => void;
};

function nationalityOptionsHtml(): string {
  return NATIONALITIES.map(
    (entry) =>
      `<option value="${entry.code}"${entry.code === 'FR' ? ' selected' : ''}>${entry.flagEmoji} ${entry.label}</option>`
  ).join('');
}

/**
 * Modale « Nouveau pilote » : prénom, nom, nationalité → POST /api/drivers.
 */
export function mountCreateDriverModal(
  host: HTMLElement,
  onCreated: (driver: Driver) => void
): CreateDriverModal {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop drivers-create-modal';
  backdrop.hidden = true;
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'driver-create-modal-title');

  backdrop.innerHTML = `
    <div class="modal drivers-create-modal-dialog">
      <div class="modal-header">
        <h2 id="driver-create-modal-title">Nouveau pilote</h2>
        <button type="button" class="btn btn-ghost drivers-create-modal-close" aria-label="Fermer">×</button>
      </div>
      <form class="modal-body drivers-create-modal-body" id="driver-create-form">
        <div class="field brutalist-container">
          <input class="brutalist-input" type="text" id="driver-create-firstname" name="firstName" placeholder="PRÉNOM" required maxlength="80" autocomplete="given-name" />
          <label class="brutalist-label" for="driver-create-firstname">Prénom</label>
        </div>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="text" id="driver-create-lastname" name="lastName" placeholder="NOM" required maxlength="80" autocomplete="family-name" />
          <label class="brutalist-label" for="driver-create-lastname">Nom</label>
        </div>
        <div class="field brutalist-container">
          <select class="brutalist-select" id="driver-create-nationality" name="nationality" required>
            ${nationalityOptionsHtml()}
          </select>
          <label class="brutalist-label" for="driver-create-nationality">Nationalité</label>
        </div>
        <p class="drivers-create-status" id="driver-create-status" role="alert" hidden></p>
      </form>
      <div class="modal-footer drivers-create-modal-footer"></div>
    </div>`;

  host.appendChild(backdrop);

  const form = backdrop.querySelector('#driver-create-form') as HTMLFormElement;
  const status = backdrop.querySelector('#driver-create-status') as HTMLElement;
  const firstNameInput = backdrop.querySelector('#driver-create-firstname') as HTMLInputElement;
  const lastNameInput = backdrop.querySelector('#driver-create-lastname') as HTMLInputElement;
  const nationalitySelect = backdrop.querySelector(
    '#driver-create-nationality'
  ) as HTMLSelectElement;
  const footer = backdrop.querySelector('.drivers-create-modal-footer') as HTMLElement;

  const submitBtn = createPillButton('Créer', { variant: 'cta' });
  footer.appendChild(submitBtn);
  const btn = submitBtn as HTMLButtonElement;

  const resetStatus = (): void => {
    status.hidden = true;
    status.textContent = '';
  };

  const close = (): void => {
    backdrop.hidden = true;
    resetStatus();
  };

  const open = (): void => {
    resetStatus();
    form.reset();
    nationalitySelect.value = 'FR';
    backdrop.hidden = false;
    firstNameInput.focus();
  };

  backdrop.querySelector('.drivers-create-modal-close')?.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  backdrop.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  const submit = (): void => {
    resetStatus();
    const firstName = firstNameInput.value.trim();
    const lastName = lastNameInput.value.trim();
    const nationality = nationalitySelect.value.trim().toUpperCase();
    if (!firstName || !lastName || nationality.length !== 2) {
      status.hidden = false;
      status.textContent = 'Prénom, nom et nationalité sont requis.';
      return;
    }
    btn.disabled = true;
    void createDriver({
      firstName,
      lastName,
      nationality,
      pilotage: 2,
      defense: 2,
      division: 1,
    })
      .then((driver) => {
        close();
        onCreated(driver);
      })
      .catch((err) => {
        status.hidden = false;
        status.textContent = err instanceof Error ? err.message : 'Création impossible.';
      })
      .finally(() => {
        btn.disabled = false;
      });
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });
  btn.addEventListener('click', () => form.requestSubmit());

  return { open, close };
}
